import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";

export const Route = createFileRoute("/university/")({
  component: UniversityPage,
  head: () => ({
    meta: [
      { title: "Alchema for Universities — Free AI Career Tools for Students" },
      {
        name: "description",
        content:
          "Partner with Alchema to give every student a personal AI career coach. EU data processing (see Privacy Policy), zero procurement cost.",
      },
    ],
  }),
});

const CHECK = (
  <svg className="h-4 w-4 flex-shrink-0 text-status-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

function UniversityPage() {
  const [formData, setFormData] = useState({
    universityName: "",
    contactName: "",
    contactEmail: "",
    role: "",
    studentCount: "" as "" | "<5000" | "5000-15000" | "15000-30000" | "30000+",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const submitMutation = useMutation(
    orpc.university.submitInquiry.mutationOptions(),
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitMutation.mutate(
      {
        universityName: formData.universityName,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        role: formData.role || undefined,
        studentCount: formData.studentCount || undefined,
        message: formData.message || undefined,
      },
      { onSuccess: () => setSubmitted(true) },
    );
  }

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <section className="mx-auto max-w-5xl px-6 pb-24 pt-20">
        {/* Hero */}
        <div className="text-center">
          <h1 className="animate-fade-up text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: "var(--text)" }}>
            Alchema for Universities
          </h1>
          <p className="animate-fade-up stagger-1 mx-auto mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Give every student a personal AI career coach — free to start, EU data processing (see Privacy Policy), zero procurement cost.
          </p>
        </div>

        {/* Value Proposition Grid */}
        <div className="animate-fade-up stagger-2 mt-16 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {[
            {
              title: "Boost Placement Rates",
              desc: "AI resume tailoring + ATS scoring helps students craft applications that get through automated screening systems.",
            },
            {
              title: "Save Advisor Time",
              desc: "Automate CV and cover letter reviews so career advisors can focus on strategic guidance, not formatting fixes.",
            },
            {
              title: "EU Data Protection",
              desc: "AI and data processing take place in EU; current provider, region, and transfer details are in our Privacy Policy. Built for European data protection standards.",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="rounded-[13px] border border-[var(--border)] p-6"
              style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
            >
              <h3 className="text-base font-semibold" style={{ color: "var(--text)" }}>{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Partnership Tiers */}
        <div className="mt-20">
          <h2 className="text-center text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
            Partnership Tiers
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              {
                name: "Free Integration",
                price: "EUR 0",
                features: ["Branded landing page", "Bonus credits for students", "Quarterly usage reports", "Dedicated partner link"],
              },
              {
                name: "Sponsored",
                price: "EUR 0 from university",
                features: ["Everything in Free", "On-campus workshops", "Student ambassadors", "Newsletter features", "Priority support"],
                highlighted: true,
              },
              {
                name: "Institutional License",
                price: "Custom",
                features: ["Everything in Sponsored", "Bulk student licensing", "Admin dashboard", "SSO integration (future)", "SLA guarantee"],
              },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`rounded-[13px] border p-6 ${tier.highlighted ? "border-[var(--accent)]" : "border-[var(--border)]"}`}
                style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
              >
                <h3 className="text-base font-semibold" style={{ color: "var(--text)" }}>{tier.name}</h3>
                <p className="mt-1 text-2xl font-bold" style={{ color: "var(--text)" }}>{tier.price}</p>
                <ul className="mt-4 space-y-2">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                      {CHECK}
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* GDPR Compliance Callout */}
        <div
          className="mt-20 rounded-[13px] border border-[var(--border)] p-8"
          style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
        >
          <h2 className="text-xl font-bold" style={{ color: "var(--text)" }}>
            GDPR & EU AI Act Compliance
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              "Data processed in EU (see Privacy Policy)",
              "AI by Mistral (EU-based provider, see Privacy Policy)",
              "EU data processing under GDPR-aligned mechanisms (see Privacy Policy)",
              "AVV/DPA available on request",
              "EU AI Act: not high-risk, human-in-the-loop",
              "Student data export & deletion on demand",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                {CHECK}
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div className="mt-20" id="contact">
          <h2 className="text-center text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
            Get in Touch
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-center text-sm" style={{ color: "var(--text-secondary)" }}>
            Interested in a partnership? Fill out the form and we'll set up your custom landing page within 48 hours.
          </p>

          {submitted ? (
            <div className="mx-auto mt-8 max-w-lg rounded-[13px] border border-status-success-bg bg-status-success-bg p-8 text-center">
              <p className="text-base font-medium" style={{ color: "var(--text)" }}>
                Thank you! We'll be in touch within 48 hours.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mx-auto mt-8 max-w-lg space-y-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="uni-name" className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                    University Name *
                  </label>
                  <input
                    id="uni-name"
                    type="text"
                    required
                    value={formData.universityName}
                    onChange={(e) => setFormData({ ...formData, universityName: e.target.value })}
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
                    placeholder="TH Augsburg"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="contact-name" className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                    Your Name *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label htmlFor="contact-email" className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="contact-role" className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                    Role
                  </label>
                  <input
                    id="contact-role"
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
                    placeholder="Leitung Karriereservice"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="student-count" className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                  Approximate Student Count
                </label>
                <select
                  id="student-count"
                  value={formData.studentCount}
                  onChange={(e) => setFormData({ ...formData, studentCount: e.target.value as typeof formData.studentCount })}
                  className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
                >
                  <option value="">Select...</option>
                  <option value="<5000">&lt; 5,000</option>
                  <option value="5000-15000">5,000 - 15,000</option>
                  <option value="15000-30000">15,000 - 30,000</option>
                  <option value="30000+">30,000+</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="inquiry-message" className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>
                  Message
                </label>
                <textarea
                  id="inquiry-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
                  placeholder="Tell us about your career service goals..."
                />
              </div>

              {submitMutation.isError && (
                <p className="text-sm" style={{ color: "var(--error)" }}>
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="w-full rounded-[10px] px-4 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "var(--accent)" }}
              >
                {submitMutation.isPending ? "Sending..." : "Send Partnership Inquiry"}
              </button>
            </form>
          )}
        </div>

        {/* Footer CTA */}
        <p className="mt-16 text-center text-sm" style={{ color: "var(--text-muted)" }}>
          Already a partner? Your students can sign up at{" "}
          <span className="font-medium" style={{ color: "var(--text-secondary)" }}>alchema.ai/uni/your-slug</span>
        </p>
      </section>
    </div>
  );
}
